import { useCallback } from 'react';

import { useRouter } from '@/i18n/routing';
import { normalizeUserRole } from '@/lib/auth/roles';
import type { UserProfile } from '@/types';

/**
 * Utility function to redirect users based on their role after authentication.
 * Enforces: admin → /admin/dashboard, provider → /provider/dashboard, user → /dashboard
 */
export function getRedirectPath(userProfile: UserProfile | null): string {
  if (!userProfile) {
    return '/auth/signin';
  }

  const role = normalizeUserRole(userProfile.role);
  let redirectPath: string;

  switch (role) {
    case 'admin':
      redirectPath = '/admin/dashboard';
      break;
    case 'provider':
      redirectPath = '/provider/dashboard';
      break;
    case 'user':
      redirectPath = '/dashboard';
      break;
    default:
      // Fallback to signin for invalid roles
      redirectPath = '/auth/signin';
  }

  return redirectPath;
}

/**
 * Hook to handle role-based redirects after authentication.
 */
export function useAuthRedirect() {
  const router = useRouter();

  const redirectBasedOnRole = useCallback(
    (userProfile: UserProfile | null) => {
      const redirectPath = getRedirectPath(userProfile);
      router.push(redirectPath);
    },
    [router],
  );

  return { redirectBasedOnRole, getRedirectPath };
}
